import { defineComponent as _defineComponent } from 'vue'
import Button from '@/components/buttons/Button.vue';
import ButtonMenu from '@/components/buttons/ButtonMenu.vue';
import EmployeeAvatar from '@/components/employee/EmployeeAvatar.vue';
import InputGroup from '@/components/form/InputGroup.vue';
import JoinedInputWrapper from '@/components/form/JoinedInputWrapper.vue';
import RepeatShifts from '@/components/form/RepeatShifts.vue';
import ShiftAdvancedOptions from '@/components/form/ShiftAdvancedOptions.vue';
import IconMessageBlock from '@/components/info/IconMessageBlock.vue';
import InlineDialog from '@/components/info/InlineDialog.vue';
import Checkbox from '@/components/inputs/Checkbox.vue';
import SelectInput from '@/components/inputs/SelectInput.vue';
import TimePicker from '@/components/inputs/TimePicker.vue';
import BubbleIcon from '@/components/interface/BubbleIcon.vue';
import i18n from '@/i18n';
import {
  CurrentEmployeeStatuses,
  fullName,
} from '@/lib/employee/employeeFunctions';
import { AvatarSizes } from '@/lib/enum/AvatarSizes';
import { InlineDialogTheme } from '@/lib/enum/Colour';
import { Icon } from '@/lib/enum/Icon';
import {
  shiftAreasAtLocation,
  shiftSessionsInsideScheduledTimes,
  shiftSessionsNoOverlap,
} from '@/lib/forms/customValidators/shiftSessionsValidators';
import { Now } from '@/lib/helpers/now';
import { formatDateToTimeString } from '@/lib/input/timePickerFunctions';
import {
  companyCanAccess,
  Feature,
} from '@/lib/permission/companyAccessFeatures';
import { Entity } from '@/lib/realtime/RealtimeEntity';
import { realtimeQuery } from '@/lib/realtime/weak/realtimeFunctions';
import {
  convertShiftTemplateAreaSessionToShiftAreaSession,
  convertShiftTemplateBreakToScheduledBreak,
  convertShiftTemplateTimesToDates,
} from '@/lib/shift-templates/shiftTemplateFunctions';
import { getErrorMessages, required } from '@/plugins/vuelidate';
import store from '@/store';
import { RepeatShiftDataInterface } from '@/types/shift';
import { ShiftAdvancedOptionsFormType } from '@/types/shiftAdvancedOptions';
import {
  ShiftFormTabEnum,
  ShiftFormV2Type,
  ShiftSessionFormType,
  ShiftSessionTypeEnum,
} from '@/types/shiftFormV2';
import {
  ScheduledBreakFormItemType,
  ShiftAreaSessionFormItemType,
} from '@/types/shiftSessionsForm';
import { sortBy } from '@/util/arrayFunctions';
import { addDays, isSameDayOrAfter } from '@/util/dateArithmetic';
import { getEntity } from '@/util/entityFunctions';
import { viewport } from '@/util/windowFunctions';
import ShiftSessionsForm from '@/views/schedule/dialogs/components/ShiftSessionsForm.vue';
import ShiftSessionsList from '@/views/schedule/dialogs/components/ShiftSessionsList.vue';
import { useVuelidate } from '@vuelidate/core';
import spacetime from 'spacetime';
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import {
  CompanySettingTimeFormatEnum,
  Employee,
  JobRole,
  Location,
  Schedule,
  ScheduledBreak,
  Shift,
  ShiftTemplate,
} from '../../../../../api/v1';


export default /*#__PURE__*/_defineComponent({
  __name: 'ShiftFormV2',
  props: {
    tab: null,
    employee: { default: null },
    shift: { default: null },
    date: null,
    startDate: { default: null },
    scheduleId: { default: null },
    jobRoleId: { default: null },
    disableJobRole: { type: Boolean, default: false },
    locationId: { default: null },
    isOpenShift: { type: Boolean, default: false },
    shiftTemplate: { default: null },
    shiftAreaSessions: { default: () => [] },
    allowAllOptions: { type: Boolean, default: false },
    showScheduleInput: { type: Boolean, default: false },
    showEmployeeInput: { type: Boolean, default: false },
    showRepeatShiftInput: { type: Boolean, default: false }
  },
  emits: ["updateAdvancedOptionsTotal", "repeatShiftsDataUpdated", "showDaysToRepeat", "updateEmployeeId"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'updateAdvancedOptionsTotal', v: number): void;
  (
    e: 'repeatShiftsDataUpdated',
    repeatShiftData: RepeatShiftDataInterface[],
  ): void;
  (e: 'showDaysToRepeat', value: boolean): void;
  (e: 'updateEmployeeId', v: number): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { tab: ShiftFormTabEnum, employee: Employee | null, shift: Shift | null, date: Date, startDate: Date | null, scheduleId: number, jobRoleId: number, disableJobRole: boolean, locationId: number | null, isOpenShift: boolean, shiftTemplate: ShiftTemplate, shiftAreaSessions: {
      startTime: Date;
      endTime: Date;
      shiftAreaId: number;
    }[], allowAllOptions: boolean, showScheduleInput: boolean, showEmployeeInput: boolean, showRepeatShiftInput: boolean }





const timePickerOpen = ref(false);
const now = new Now();

const timezone = computed<string>(() => store.getters.timezone);

const jobRoles = reactive(realtimeQuery(Entity.JobRole).whereActive().fetch());
const locations = reactive(
  realtimeQuery(Entity.Location).whereActive().fetch(),
);
const schedules = reactive(
  realtimeQuery(Entity.Schedule).whereActive().fetch(),
);
const activeShiftAreas = reactive(
  realtimeQuery(Entity.ShiftArea).whereActive().fetch(),
);
const currentEmployees = realtimeQuery(Entity.Employee)
  .where('status', 'in', CurrentEmployeeStatuses)
  .fetch();

const employeeList = computed(() => {
  return [
    { id: 0, name: i18n.tc('label.unassignedShift') },
    { id: -1, name: i18n.tc('label.openShift') },
    ...currentEmployees.data
      .filter((e) => e.scheduleIds.includes(props.scheduleId))
      .filter((e) => !props.jobRoleId || e.jobRoleIds.includes(props.jobRoleId))
      .filter(
        (employee) =>
          !employee.joinDate ||
          isSameDayOrAfter(
            props.date,
            employee.joinDate.toDate(timezone.value),
            timezone.value,
          ),
      )
      .map((e) => ({
        id: e.id,
        name: fullName(e),
      })),
  ];
});

const defaultBreakLength = computed<number>(
  () => store.state.settings.scheduleSettings.defaultBreakLength,
);
const timeFormat = computed<CompanySettingTimeFormatEnum>(
  () => store.getters['settings/timeFormat'],
);

// declare defaultValues early to use inside form and also for providing into patchRequest method
const defaultValues = computed<ShiftFormV2Type>(() => ({
  locationId: props.shift?.locationId ?? props.locationId ?? null,
  requiresApproval: props.shift?.requiresApproval ?? false,
  scheduleId: props.shift?.scheduleId ?? props.scheduleId,
  employeeId: props.shift?.employeeId ?? props.employee?.id ?? null,
  jobRoleId:
    props.shift?.jobRoleId ??
    props.jobRoleId ??
    props.employee?.defaultJobRoleId ??
    null,
  tags: props.shift?.tagIds ?? [],
  startsAt:
    props.shift?.startsAt ||
    (props.shiftAreaSessions?.length
      ? sortBy(props.shiftAreaSessions, 'startTime')[0].startTime
      : props.startDate || null),
  endsAt:
    props.shift?.endsAt ||
    (props.shiftAreaSessions?.length
      ? sortBy(props.shiftAreaSessions, '-endTime')[0].endTime
      : null),
  showEndTime: props.shift?.showEndTime ?? true,
  showJobRole: props.shift?.showJobRole ?? true,
  published: !!props.shift?.publishedAt,
  scheduledBreaks: props.shift?.scheduledBreaks ?? [],
  shiftAreaSessions:
    props.shift?.shiftAreaSessions ??
    props.shiftAreaSessions.map((session, i) => ({ ...session, id: i })) ??
    [],
  notes: props.shift?.notes ?? '',
}));

const form = reactive<ShiftFormV2Type>({
  // Clone the default values so we don't accidentally update them by reference when changing break data
  ...structuredClone(defaultValues.value),
});

const repeatedDayIndexes = ref<number[]>([0]);
const showRepeatDays = ref<boolean>(false);

// used by ShiftAdvancedOptions component when mounted
const advancedOptions = computed<ShiftAdvancedOptionsFormType>(() => ({
  showEndTime: form.showEndTime,
  showJobRole: form.showJobRole,
  tags: form.tags,
  notes: form.notes,
}));
// used by ShiftSessionsForm for new sessions
const newBreakSessions = ref<ScheduledBreakFormItemType[]>([]);
const newShiftAreaSessions = ref<ShiftAreaSessionFormItemType[]>([]);

const addScheduledBreak = () => {
  newBreakSessions.value = [
    ...newBreakSessions.value,
    {
      id: Date.now(),
      paid: false,
      durationInMinutes: defaultBreakLength.value,
      breakStartTimeEnabled: false,
      startTime: '',
    },
  ];
};

const addShiftAreaSession = () => {
  newShiftAreaSessions.value = [
    ...newShiftAreaSessions.value,
    {
      id: Date.now(),
      shiftAreaId: null,
      startTime: null,
      endTime: null,
    },
  ];
};

const addShiftSessionMenu = computed(() => {
  return [
    {
      id: 'addBreak',
      value: i18n.tc('button.addBreak'),
      icon: Icon.Mug,
      action: addScheduledBreak,
      enabled: true,
    },
    {
      id: 'addShiftArea',
      value: i18n.tc('button.addArea'),
      icon: Icon.ChartHorizontal,
      action: addShiftAreaSession,
      enabled:
        companyCanAccess(Feature.ShiftAreas) && activeShiftAreas.data.length,
    },
  ].filter((a) => a.enabled);
});

// used by ShiftSessionsList to edit a session
const shiftSessionToEdit = ref<ShiftSessionFormType | null>(null);

const validations = {
  locationId: { required },
  requiresApproval: {},
  scheduleId: {},
  employeeId: {},
  jobRoleId: { required },
  startsAt: { required },
  endsAt: { required },
  published: {},
  scheduledBreaks: {
    shiftSessionsNoOverlap: shiftSessionsNoOverlap(timezone.value),
    shiftSessionsInsideScheduledHours: shiftSessionsInsideScheduledTimes(
      timezone.value,
    ),
  },
  shiftAreaSessions: {
    shiftSessionsNoOverlap: shiftSessionsNoOverlap(timezone.value),
    shiftSessionsInsideScheduledHours: shiftSessionsInsideScheduledTimes(
      timezone.value,
    ),
    shiftAreasAtLocation: shiftAreasAtLocation(activeShiftAreas.data),
  },
  tags: {},
  showEndTime: {},
  showJobRole: {},
  notes: {},
};
const v$ = useVuelidate(validations, form, { $autoDirty: true });

// Map the selected shiftTemplate to valid ShiftFormV2Type data
const shiftTemplateFormData = computed<Partial<ShiftFormV2Type> | null>(() => {
  if (props.shiftTemplate) {
    const {
      jobRoleId,
      showJobRole,
      tagIds,
      notes,
      locationId,
      shiftTemplateBreaks,
      shiftTemplateAreaSessions,
    } = props.shiftTemplate;

    const { startsAt, endsAt, showEndTime } = convertShiftTemplateTimesToDates(
      props.date,
      props.shiftTemplate,
      timezone.value,
    );
    const scheduledBreaks = shiftTemplateBreaks.map((tb, i) =>
      convertShiftTemplateBreakToScheduledBreak(
        startsAt,
        tb,
        timezone.value,
        i,
      ),
    ) as ScheduledBreak[];
    const shiftAreaSessions = shiftTemplateAreaSessions.map((ta, i) =>
      convertShiftTemplateAreaSessionToShiftAreaSession(
        startsAt,
        ta,
        timezone.value,
        i + props.shiftAreaSessions.length,
      ),
    );

    return {
      locationId,
      jobRoleId,
      startsAt,
      endsAt,
      tags: tagIds,
      notes: notes ?? '',
      showEndTime,
      showJobRole,
      scheduledBreaks,
      shiftAreaSessions,
    };
  }
  return null;
});

const availableJobRoles = computed<JobRole[]>(() => {
  const jobRoleList = jobRoles.data ?? [];
  if (props.allowAllOptions) {
    jobRoleList.unshift({
      id: 0,
      name: i18n.tc('select.all'),
    });
  }
  if (!props.employee) {
    return jobRoleList;
  }
  return jobRoleList.filter((j) => props.employee.jobRoleIds.includes(j.id));
});

const availableLocations = computed<Location[]>(() => {
  const locationList = locations.data ?? [];
  if (props.allowAllOptions) {
    locationList.unshift({
      id: 0,
      name: i18n.tc('select.all'),
    });
  }
  if (!props.employee) {
    return locationList;
  }
  return locationList.filter((l) => props.employee.locationIds.includes(l.id));
});

const availableSchedules = computed<Schedule[]>(() => {
  const scheduleList = schedules.data ?? [];
  if (props.allowAllOptions) {
    scheduleList.unshift({
      id: 0,
      name: i18n.tc('select.all'),
    });
  }
  if (!props.employee) {
    return scheduleList;
  }
  return scheduleList.filter((s) => props.employee.scheduleIds.includes(s.id));
});

const invalidShiftTimeMessage = computed<string>(() => {
  const { startsAt, endsAt } = form;
  if (!props.shift && startsAt && startsAt < now.time) {
    return i18n.tc('errorMessage.shiftStartFuture');
  }
  if (!startsAt || !endsAt) {
    return '';
  }
  return '';
});

const totalNewSessions = computed<number>(
  () => [...newBreakSessions.value, ...newShiftAreaSessions.value].length,
);
const totalShiftSessions = computed<number>(
  () => [...form.scheduledBreaks, ...form.shiftAreaSessions].length,
);

const updateDefaultLocationId = () => {
  if (props.shift) {
    form.locationId = props.shift.locationId;
    return;
  }
  if (props.locationId) {
    form.locationId = props.locationId;
    return;
  }
  if (props.scheduleId) {
    const { defaultLocationId } =
      getEntity(props.scheduleId, schedules.data) ?? {};
    if (availableLocations.value.find((l) => l.id === defaultLocationId)) {
      form.locationId = defaultLocationId;
      return;
    }
  }
  form.locationId = availableLocations.value[0]?.id;
};

const updateDefaultJobRoleId = () => {
  if (props.shift) {
    form.jobRoleId = props.shift.jobRoleId;
    return;
  }
  if (props.employee) {
    form.jobRoleId = props.employee.defaultJobRoleId;
    return;
  }
  form.jobRoleId = availableJobRoles.value[0].id;
};

const updateFormWithShiftTemplateData = () => {
  Object.keys(shiftTemplateFormData.value).forEach((field) => {
    if (field === 'locationId' && props.shiftAreaSessions.length) {
      return;
    }
    form[field] = shiftTemplateFormData.value[field];
    if (field === 'shiftAreaSessions' && props.shiftAreaSessions.length) {
      form[field].push(
        ...props.shiftAreaSessions.map((a, i) => ({ ...a, id: i })),
      );
    }
  }, {});

  // Update default values if the template does not have these filled or no longer active
  const { locationId, jobRoleId } = shiftTemplateFormData.value;
  if (
    !props.shiftAreaSessions.length &&
    (!locationId ||
      !availableLocations.value.map((l) => l.id).includes(locationId))
  ) {
    updateDefaultLocationId();
  }
  if (
    !jobRoleId ||
    !availableJobRoles.value.map((r) => r.id).includes(jobRoleId)
  ) {
    updateDefaultJobRoleId();
  }
};

onMounted(() => {
  now.init();
});

onBeforeUnmount(() => {
  now.destroy();
});

const formatTime = (date: Date | null): string => {
  return formatDateToTimeString(date, timeFormat.value, timezone.value);
};

const validateTime = (time: string): boolean => {
  if (!time) return false;
  const hour = time.slice(0, time.indexOf(':'));
  const minute = time.slice(time.indexOf(':') + 1, time.indexOf(':') + 3);
  return [hour, minute].every((t) => Number(t) || Number(t) === 0);
};

const updateTime = (field: 'startsAt' | 'endsAt', time: string): void => {
  v$.value[field].$touch();

  if (!validateTime(time)) {
    form[field] = null;
    return;
  }

  let newDate = spacetime(props.date, timezone.value).time(time).toNativeDate();

  // If field is startsAt, and we have an endsAt value, but the new chosen date is more than the current end,
  // add a day to the endsAt value
  const { startsAt, endsAt } = form;
  if (endsAt && field === 'startsAt' && newDate >= endsAt) {
    form.endsAt = spacetime(endsAt, timezone.value)
      .add(1, 'day')
      .toNativeDate();
  }
  // If field is startsAt, and we have an endsAt value, and the new chosen date is less than the current end,
  // the end date can be the same day as the start day
  else if (endsAt && field === 'startsAt' && newDate < endsAt) {
    const newEnd = spacetime(newDate, timezone.value)
      .time(String(spacetime(endsAt, timezone.value).time()))
      .toNativeDate();
    if (newDate < newEnd) form.endsAt = newEnd;
  }
  // If field is endsAt, and we have a startsAt value, but the new chosen date is less than the current start,
  // add a day to the end
  else if (startsAt && field === 'endsAt' && newDate <= startsAt) {
    newDate = addDays(newDate, 1, timezone.value);
  }

  form[field] = newDate;
};

const convertBreakFormToScheduledBreak = (
  breakToParse: ScheduledBreakFormItemType,
): ScheduledBreak => {
  const { id, shiftId, companyId, paid, durationInMinutes, startTime } =
    breakToParse;
  let breakStartsAt: Date | null = null;

  if (startTime) {
    const start = spacetime(props.date, timezone.value).time(startTime);
    breakStartsAt = start.toNativeDate();

    const { startsAt } = form;
    if (start.isBefore(startsAt)) {
      breakStartsAt = addDays(breakStartsAt, 1, timezone.value);
    }
  }
  // add id, shiftId and companyId fields as now those are the part of ScheduledBreak interface
  return {
    id,
    shiftId,
    companyId,
    paid,
    durationInMinutes,
    startsAt: breakStartsAt,
  };
};

const updateNewBreakSessions = (breaks: ScheduledBreakFormItemType[]): void => {
  newBreakSessions.value = breaks;
};
const updateNewShiftAreaSessions = (
  sessions: ShiftAreaSessionFormItemType[],
): void => {
  newShiftAreaSessions.value = sessions;
};
const mergeNewSessions = (): void => {
  form.scheduledBreaks = [
    ...form.scheduledBreaks,
    ...newBreakSessions.value.map(convertBreakFormToScheduledBreak),
  ];
  newBreakSessions.value = [];

  form.shiftAreaSessions = [
    ...form.shiftAreaSessions,
    ...newShiftAreaSessions.value,
  ];
  newShiftAreaSessions.value = [];
};

const clearNewSessions = (): void => {
  newBreakSessions.value = [];
  newShiftAreaSessions.value = [];
};

const removeBreak = (breakId: number) => {
  form.scheduledBreaks = form.scheduledBreaks.filter((b) => b.id !== breakId);
  shiftSessionToEdit.value = null;
};
const removeArea = (areaId: number) => {
  form.shiftAreaSessions = form.shiftAreaSessions.filter(
    (a) => a.id !== areaId,
  );
  shiftSessionToEdit.value = null;
};
const updateShiftSession = () => {
  if (!shiftSessionToEdit.value) {
    return;
  }
  if (shiftSessionToEdit.value.type === ShiftSessionTypeEnum.Break) {
    const indexOfBreak = form.scheduledBreaks.findIndex(
      (b) => b.id === shiftSessionToEdit.value.form.id,
    );
    form.scheduledBreaks.splice(
      indexOfBreak,
      1,
      convertBreakFormToScheduledBreak(shiftSessionToEdit.value.form),
    );
    shiftSessionToEdit.value = null;
  }
  if (shiftSessionToEdit.value.type === ShiftSessionTypeEnum.Area) {
    const indexOfArea = form.shiftAreaSessions.findIndex(
      (a) => a.id === shiftSessionToEdit.value.form.id,
    );
    Object.assign(
      form.shiftAreaSessions[indexOfArea],
      shiftSessionToEdit.value.form,
    );
    shiftSessionToEdit.value = null;
  }
  v$.value.$touch();
};

const updateAdvancedOptions = (options: ShiftAdvancedOptionsFormType) => {
  Object.assign(form, options);
};

const updateRepeatedDays = (): void => {
  if (form.startsAt === null || form.endsAt === null) {
    return;
  }

  let modifiedScheduledBreaks: ScheduledBreak[] = [];
  let modifiedShiftAreas: ShiftAreaSessionFormItemType[] = [];

  const calculatedRepeatShiftData: RepeatShiftDataInterface[] =
    repeatedDayIndexes.value.reduce(
      (array, day, index) => {
        if (index === 0) {
          return array;
        }

        const calculatedShiftTimes = {
          startsAt: spacetime(form.startsAt, timezone.value)
            .add(day, 'days')
            .toNativeDate(),
          endsAt: spacetime(form.endsAt, timezone.value)
            .add(day, 'days')
            .toNativeDate(),
        };

        if (form.scheduledBreaks.length) {
          modifiedScheduledBreaks = form.scheduledBreaks.reduce(
            (acc, scheduledBreak) => {
              // calculate the correct dates for repeated breaks with set start time
              if (scheduledBreak.startsAt !== null) {
                return [
                  ...acc,
                  {
                    ...scheduledBreak,
                    startsAt: spacetime(scheduledBreak.startsAt, timezone.value)
                      .add(day, 'days')
                      .toNativeDate(),
                  },
                ];
              }
              return [...acc, scheduledBreak];
            },
            [],
          );
        }

        if (form.shiftAreaSessions.length) {
          modifiedShiftAreas = form.shiftAreaSessions.map(
            (shiftAreaSession) => {
              return {
                ...shiftAreaSession,
                startTime: spacetime(shiftAreaSession.startTime, timezone.value)
                  .add(day, 'days')
                  .toNativeDate(),
                endTime: spacetime(shiftAreaSession.endTime, timezone.value)
                  .add(day, 'days')
                  .toNativeDate(),
              };
            },
          );
        }
        return [
          ...array,
          {
            ...calculatedShiftTimes,
            ...(modifiedScheduledBreaks.length && {
              scheduledBreaks: [...modifiedScheduledBreaks],
            }),
            ...(modifiedShiftAreas.length && {
              shiftAreaSessions: [...modifiedShiftAreas],
            }),
          },
        ];
      },
      [
        {
          startsAt: form.startsAt,
          endsAt: form.endsAt,
        },
      ],
    );
  emit('repeatShiftsDataUpdated', calculatedRepeatShiftData);
};

const showDaysToRepeatHandler = (value: boolean) => {
  showRepeatDays.value = value;
  emit('showDaysToRepeat', value);
};

// update form with new shift template data
watch(
  () => props.shiftTemplate,
  (newShiftTemplate) => {
    if (newShiftTemplate) {
      updateFormWithShiftTemplateData();
    }
  },
  { immediate: true },
);

watch(
  advancedOptions,
  () => {
    const { showJobRole, showEndTime, tags, notes } = advancedOptions.value;
    const total =
      Number(!showJobRole) +
      Number(!showEndTime) +
      tags.length +
      Number(!!notes.length);
    emit('updateAdvancedOptionsTotal', total);
  },
  { immediate: true },
);

watch(
  () => props.date,
  () => {
    const { startsAt, endsAt } = form;
    if (startsAt) updateTime('startsAt', formatTime(startsAt));
    if (endsAt) updateTime('endsAt', formatTime(endsAt));
  },
  { deep: true },
);

watch(
  () => props.shift,
  (newShift, oldShift) => {
    updateDefaultLocationId();
    if (oldShift && newShift.employeeId !== oldShift.employeeId) {
      form.employeeId = newShift.employeeId;
    }
  },
  { immediate: true, deep: true },
);

// if creating unassigned/open shift, use first item of job role list
watch(
  () => jobRoles.isLoading,
  (loading) => {
    if (!loading && !form.jobRoleId && availableJobRoles.value.length) {
      updateDefaultJobRoleId();
      v$.value.jobRoleId.$reset();
    }
  },
  { immediate: true },
);

watch(
  schedules,
  () => {
    updateDefaultLocationId();
  },
  { immediate: true },
);

watch(
  availableLocations,
  () => {
    updateDefaultLocationId();
  },
  { immediate: true },
);

watch(form, () => v$.value.$touch(), { deep: true });

watch(
  [
    () => form.startsAt,
    () => form.endsAt,
    () => form.shiftAreaSessions,
    () => form.scheduledBreaks,
    repeatedDayIndexes,
  ],
  () => {
    if (props.showRepeatShiftInput) {
      updateRepeatedDays();
    }
  },
  { deep: true, immediate: true },
);

return { __sfc: true,props, emit, timePickerOpen, now, timezone, jobRoles, locations, schedules, activeShiftAreas, currentEmployees, employeeList, defaultBreakLength, timeFormat, defaultValues, form, repeatedDayIndexes, showRepeatDays, advancedOptions, newBreakSessions, newShiftAreaSessions, addScheduledBreak, addShiftAreaSession, addShiftSessionMenu, shiftSessionToEdit, validations, v$, shiftTemplateFormData, availableJobRoles, availableLocations, availableSchedules, invalidShiftTimeMessage, totalNewSessions, totalShiftSessions, updateDefaultLocationId, updateDefaultJobRoleId, updateFormWithShiftTemplateData, formatTime, validateTime, updateTime, convertBreakFormToScheduledBreak, updateNewBreakSessions, updateNewShiftAreaSessions, mergeNewSessions, clearNewSessions, removeBreak, removeArea, updateShiftSession, updateAdvancedOptions, updateRepeatedDays, showDaysToRepeatHandler, Button, ButtonMenu, EmployeeAvatar, InputGroup, JoinedInputWrapper, RepeatShifts, ShiftAdvancedOptions, IconMessageBlock, InlineDialog, Checkbox, SelectInput, TimePicker, BubbleIcon, i18n, AvatarSizes, InlineDialogTheme, Icon, companyCanAccess, Feature, getErrorMessages, ShiftFormTabEnum, getEntity, viewport, ShiftSessionsForm, ShiftSessionsList, CompanySettingTimeFormatEnum }
}

})